package com.example.hukakmp.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.example.hukakmp.getPlatform
import com.example.hukakmp.model.PartePesca
import com.example.hukakmp.viewmodel.ReportesViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ReportesScreen(
    uiState: ReportesViewModel.ReportesUiState,
    puedeEliminarse: (PartePesca) -> Boolean,
    onEliminar: (String) -> Unit,
    onBack: () -> Unit,
    onNuevoParte: () -> Unit,
) {
    var busqueda by remember { mutableStateOf("") }
    var mostrarMapaGeneral by remember { mutableStateOf(false) }

    if (mostrarMapaGeneral) {
        ReportsOverviewMap(
            reportes = uiState.reportes,
            onClose = { mostrarMapaGeneral = false },
            modifier = Modifier.fillMaxSize(),
        )
        return
    }

    val filtrados = remember(uiState.reportes, busqueda) {
        if (busqueda.isBlank()) {
            uiState.reportes
        } else {
            uiState.reportes.filter { reporte ->
                reporte.tipo?.contains(busqueda, ignoreCase = true) == true ||
                    reporte.peces.any { it.especie.contains(busqueda, ignoreCase = true) } ||
                    reporte.ubicacion?.nombre?.contains(busqueda, ignoreCase = true) == true
            }
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text("Mis reportes", fontWeight = FontWeight.Bold)
                        Text(
                            "Historial de jornadas",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White.copy(alpha = 0.75f),
                        )
                    }
                },
                navigationIcon = {
                    TextButton(onClick = onBack) {
                        Text("‹", color = Color.White, style = MaterialTheme.typography.headlineSmall)
                    }
                },
                actions = {
                    TextButton(onClick = { mostrarMapaGeneral = true }) {
                        Text("Mapa", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                    TextButton(onClick = onNuevoParte) {
                        Text("+ Nuevo", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    titleContentColor = MaterialTheme.colorScheme.onPrimary,
                ),
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = onNuevoParte,
                containerColor = MaterialTheme.colorScheme.secondary,
                contentColor = MaterialTheme.colorScheme.onSecondary,
            ) {
                Text("+")
            }
        },
    ) { padding ->
        Column(modifier = Modifier.fillMaxSize().padding(padding)) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(start = 16.dp, end = 16.dp, top = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                EstadisticaChip(
                    titulo = "Jornadas",
                    valor = uiState.reportes.size.toString(),
                    modifier = Modifier.weight(1f),
                )
                EstadisticaChip(
                    titulo = "Capturas",
                    valor = uiState.reportes.sumOf { it.cantidadTotal }.toString(),
                    modifier = Modifier.weight(1f),
                )
                EstadisticaChip(
                    titulo = "Especies",
                    valor = uiState.reportes.flatMap { it.peces }.map { it.especie }.distinct().size.toString(),
                    modifier = Modifier.weight(1f),
                )
            }

            OutlinedTextField(
                value = busqueda,
                onValueChange = { busqueda = it },
                label = { Text("Buscar especie, modalidad o lugar") },
                singleLine = true,
                shape = RoundedCornerShape(16.dp),
                modifier = Modifier.fillMaxWidth().padding(16.dp),
            )

            when {
                uiState.isLoading -> Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        CircularProgressIndicator()
                        Text(
                            "Cargando tus jornadas…",
                            modifier = Modifier.padding(top = 12.dp),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }

                uiState.error != null -> Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Surface(
                        modifier = Modifier.padding(24.dp),
                        shape = RoundedCornerShape(18.dp),
                        color = MaterialTheme.colorScheme.errorContainer,
                    ) {
                        Text(
                            uiState.error,
                            color = MaterialTheme.colorScheme.onErrorContainer,
                            modifier = Modifier.padding(18.dp),
                        )
                    }
                }

                filtrados.isEmpty() -> EmptyReportsState(onNuevoParte)

                else -> LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp),
                ) {
                    items(filtrados, key = { it.id ?: it.fecha.orEmpty() }) { reporte ->
                        ReporteCard(
                            reporte = reporte,
                            puedeEliminarse = puedeEliminarse(reporte),
                            onEliminar = { reporte.id?.let(onEliminar) },
                        )
                    }
                    item { Spacer(Modifier.height(88.dp)) }
                }
            }
        }
    }
}

@Composable
private fun EmptyReportsState(onNuevoParte: () -> Unit) {
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
        Surface(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            shape = RoundedCornerShape(22.dp),
            color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.65f),
        ) {
            Column(
                modifier = Modifier.padding(22.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                HukaLogoMark(tint = MaterialTheme.colorScheme.primary)
                Text(
                    "Todavía no tenés jornadas registradas",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(top = 12.dp),
                )
                Text(
                    "Cuando cargues tu primer parte, va a aparecer acá junto con tus capturas y especies.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(top = 6.dp),
                )
                TextButton(onClick = onNuevoParte, modifier = Modifier.padding(top = 8.dp)) {
                    Text("+ Crear mi primer parte")
                }
            }
        }
    }
}

@Composable
private fun EstadisticaChip(titulo: String, valor: String, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier,
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                valor,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Black,
                color = MaterialTheme.colorScheme.primary,
            )
            Text(
                titulo,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun ReporteCard(
    reporte: PartePesca,
    puedeEliminarse: Boolean,
    onEliminar: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp),
        shape = RoundedCornerShape(18.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top,
            ) {
                Column {
                    Text(
                        reporte.fecha ?: "Sin fecha",
                        style = MaterialTheme.typography.titleMedium,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold,
                    )
                    Text(
                        "${reporte.horaInicio ?: "?"} - ${reporte.horaFin ?: "?"}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                Surface(
                    shape = RoundedCornerShape(10.dp),
                    color = MaterialTheme.colorScheme.primaryContainer,
                ) {
                    Text(
                        text = if (reporte.cantidadTotal == 0) "Sin capturas" else "${reporte.cantidadTotal} capturas",
                        modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onPrimaryContainer,
                        fontWeight = FontWeight.Bold,
                    )
                }
            }

            Text(
                text = reporte.tipo?.replaceFirstChar { it.uppercase() } ?: "Modalidad no especificada",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Medium,
                modifier = Modifier.padding(top = 10.dp),
            )

            reporte.ubicacion?.nombre?.let {
                Text(
                    "Lugar: $it",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(top = 2.dp),
                )
            }

            if (reporte.cantidadTotal > 0) {
                Text(
                    text = reporte.peces.joinToString(" · ") { "${it.cantidad} ${it.especie}" },
                    style = MaterialTheme.typography.bodyMedium,
                    modifier = Modifier.padding(top = 8.dp),
                )
            }

            if (reporte.cantidadDevuelta > 0) {
                Text(
                    "${reporte.cantidadDevuelta} devueltos al agua",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.secondary,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }

            if (getPlatform().name == "Web" && reporte.fotos.isNotEmpty()) {
                Text(
                    "Fotos",
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(top = 12.dp, bottom = 6.dp),
                )
                ReportPhotoGallery(
                    urls = reporte.fotos,
                    modifier = Modifier.fillMaxWidth().height(158.dp),
                )
            }

            if (puedeEliminarse) {
                TextButton(onClick = onEliminar, modifier = Modifier.align(Alignment.End)) {
                    Text("Eliminar")
                }
            }
        }
    }
}
